// ============================================
// PAGE - Administración de Caja 360 (propietario)
// ============================================
// Turnos abiertos, historial con diferencias, aprobaciones y configuración.
// Fase 6B del módulo nuevo de turnos (turno_caja).

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Skeleton, Badge } from '@almacenes/ui';
import {
  useTurnosAbiertos,
  useTurnos,
  useMovimientosPendientes,
  useMovimientosResueltos,
  useMovimientosDeshacerLog,
  useDeshacerResolucionMovimiento,
  useConfiguracionCaja,
  useActualizarConfiguracionCaja,
  useForzarCierreTurno,
  useAprobarMovimiento,
  useRechazarMovimiento,
  useVendedoresCaja,
  useAbrirTurno,
  useCuadraturasConFiltros,
  useCerrarCajaById,
  useUIStore,
  useAuthStore,
  useMisPermisos,
  getFechaHoyChile,
  type TurnoListItem,
  type TurnoCajaEstado,
  type FiltrosTurnos,
  type AbrirTurnoDto,
  type PermisoCaja,
  type CuadraturaCaja,
} from '@almacenes/core';
import { MdArrowBack, MdLock, MdVisibility, MdCheck, MdClose as MdX, MdUndo, MdAdd, MdExpandMore, MdExpandLess, MdLightbulbOutline, MdInfoOutline } from 'react-icons/md';
import { PremiumGate } from '@/components/PremiumGate';
import { useShepherdTour, TourFab, type TourStepConfig } from '@almacenes/ui';
import { useTourPersistido } from '@/hooks/useTourPersistido';
import logo from '@/assets/logos/nuevo.svg';
import { TurnoDetalleModal } from './components/TurnoDetalleModal';
import { ForzarCierreModal } from './components/ForzarCierreModal';
import { AbrirTurnoParaVendedorModal } from './components/AbrirTurnoParaVendedorModal';
import { ConfirmarTurnoAntiguoModal } from './components/ConfirmarTurnoAntiguoModal';
import { RechazarMovimientoModal } from './components/RechazarMovimientoModal';
import { ToggleSwitch } from './components/ToggleSwitch';
import { PaginacionTabla } from './components/PaginacionTabla';
import { TableSkeletonRows } from './components/TableSkeletonRows';
import { SortableTh } from './components/SortableTh';
import { TabPermisos } from './components/TabPermisos';
import { formatCurrency, formatearFechaHora } from './utils/formato';
import { esConfirmacionTurnoAntiguo } from './utils/turnoAntiguo';
import { badgeEstadoTurno, badgeEstadoMovimiento } from './utils/badges';

type Tab = 'abiertos' | 'historial' | 'aprobaciones' | 'configuracion' | 'permisos';
type SortDirection = 'asc' | 'desc';

const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'abiertos', label: 'Turnos abiertos' },
  { id: 'historial', label: 'Historial y diferencias' },
  { id: 'aprobaciones', label: 'Aprobaciones' },
  { id: 'configuracion', label: 'Configuración' },
  { id: 'permisos', label: 'Permisos' },
];

/**
 * true si el usuario actual puede ejercer este permiso de caja. Cuando es
 * propietario, el backend ya devuelve el catálogo completo en
 * useMisPermisos() — no hace falta chequear esPropietario acá aparte.
 */
const usePuedeCaja = (permiso: PermisoCaja): boolean => {
  const { data: misPermisos } = useMisPermisos();
  return misPermisos?.includes(permiso) ?? false;
};

/** Hook local de orden por encabezado: mismo criterio que InventarioPage (toggle de dirección al repetir columna). */
const useSort = (campoInicial: string, direccionInicial: SortDirection, onCambiar: () => void) => {
  const [sortField, setSortField] = useState(campoInicial);
  const [sortDirection, setSortDirection] = useState<SortDirection>(direccionInicial);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    onCambiar();
  };

  return { sortField, sortDirection, handleSort, sortParam: `${sortField},${sortDirection}` };
};

// ---------- Tab: Turnos abiertos ----------

const TabTurnosAbiertos = () => {
  const addToast = useUIStore((state) => state.addToast);
  const puedeAbrirParaOtro = usePuedeCaja('CAJA_ABRIR_PARA_OTRO');
  const puedeCerrarForzado = usePuedeCaja('CAJA_CERRAR_FORZADO');
  const [page, setPage] = useState(0);
  const { sortField, sortDirection, handleSort, sortParam } = useSort('fechaHoraApertura', 'asc', () => setPage(0));
  const { data, isLoading, isFetching } = useTurnosAbiertos(page, sortParam);
  const actualizando = isFetching && !isLoading;
  const forzarCierre = useForzarCierreTurno();
  const abrirTurno = useAbrirTurno();

  const [turnoDetalleId, setTurnoDetalleId] = useState<number | null>(null);
  const [turnoAForzar, setTurnoAForzar] = useState<TurnoListItem | null>(null);
  const [showAbrirModal, setShowAbrirModal] = useState(false);
  // Alerta del backend: hay turnos abiertos hace más de 3 días en el almacén; se confirma antes de abrir igual
  const [confirmacionAbrir, setConfirmacionAbrir] = useState<{ dto: AbrirTurnoDto; mensaje: string } | null>(null);

  const handleForzarCierre = (motivo: string, efectivoContado: number | undefined) => {
    if (!turnoAForzar) return;
    forzarCierre.mutate(
      { turnoId: turnoAForzar.id, dto: { motivo, efectivoContado } },
      {
        onSuccess: () => {
          setTurnoAForzar(null);
          addToast('success', 'Turno cerrado');
        },
        onError: (err: any) => addToast('error', err?.message || 'Error al forzar el cierre'),
      }
    );
  };

  const handleAbrirParaVendedor = (dto: AbrirTurnoDto) => {
    abrirTurno.mutate(dto, {
      onSuccess: () => {
        setShowAbrirModal(false);
        setConfirmacionAbrir(null);
        addToast('success', 'Caja abierta para el vendedor');
      },
      onError: (err: any) => {
        if (esConfirmacionTurnoAntiguo(err)) {
          setConfirmacionAbrir({ dto, mensaje: err.message });
          return;
        }
        addToast('error', err?.message || 'Error al abrir la caja');
      },
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {puedeAbrirParaOtro && (
        <div className="flex justify-end px-6 py-4 border-b border-gray-200">
          <Button variant="secondary" onClick={() => setShowAbrirModal(true)} data-tour="admin-abrir-vendedor">
            <MdAdd className="inline-block mr-2 text-xl" />
            Abrir caja para un vendedor
          </Button>
        </div>
      )}
      {!isLoading && (!data || data.content.length === 0) ? (
        <p className="text-center py-12 text-gray-500">No hay cajas abiertas en este momento</p>
      ) : (
        <>
          <div className={`overflow-x-auto transition-opacity duration-300 ${actualizando ? 'opacity-60' : ''}`}>
            <table className="w-full">
              <thead className="bg-[#F3F4F9]">
                <tr>
                  <SortableTh field="usuarioNombre" label="Vendedor" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="fechaHoraApertura" label="Apertura" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                  <SortableTh field="montoInicial" label="Monto inicial" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} align="right" />
                  <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeletonRows columns={4} />
                ) : (
                  data!.content.map((turno, index) => (
                    <tr key={turno.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-3 text-sm font-semibold" style={{ color: '#11194F' }}>{turno.usuarioNombre}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{formatearFechaHora(turno.fechaHoraApertura)}</td>
                      <td className="px-6 py-3 text-sm text-right">{formatCurrency(turno.montoInicial)}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setTurnoDetalleId(turno.id)}
                            className="p-2 rounded-lg text-[#43BFFF] hover:bg-blue-50 transition-colors"
                            title="Ver detalle"
                          >
                            <MdVisibility className="text-xl" />
                          </button>
                          {puedeCerrarForzado && (
                            <button
                              onClick={() => setTurnoAForzar(turno)}
                              className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                              title="Cierre forzado"
                            >
                              <MdLock className="text-xl" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {data && (
            <PaginacionTabla
              page={page}
              totalPages={data.totalPages}
              totalElements={data.totalElements}
              cantidadEnPagina={data.content.length}
              etiqueta="turnos abiertos"
              onChange={setPage}
            />
          )}
        </>
      )}

      {turnoDetalleId !== null && (
        <TurnoDetalleModal turnoId={turnoDetalleId} onClose={() => setTurnoDetalleId(null)} />
      )}
      {turnoAForzar && (
        <ForzarCierreModal
          turno={turnoAForzar}
          onClose={() => setTurnoAForzar(null)}
          onConfirmar={handleForzarCierre}
          isLoading={forzarCierre.isPending}
        />
      )}
      {showAbrirModal && (
        <AbrirTurnoParaVendedorModal
          onClose={() => setShowAbrirModal(false)}
          onConfirmar={handleAbrirParaVendedor}
          isLoading={abrirTurno.isPending}
        />
      )}
      <ConfirmarTurnoAntiguoModal
        mensaje={confirmacionAbrir?.mensaje ?? null}
        onConfirm={() => handleAbrirParaVendedor({ ...confirmacionAbrir!.dto, confirmado: true })}
        onCancel={() => setConfirmacionAbrir(null)}
        isLoading={abrirTurno.isPending}
      />
    </div>
  );
};

// ---------- Tab: Historial y diferencias ----------

const diferenciaColor = (diferencia: number) => {
  if (diferencia > 0) return 'text-green-600';
  if (diferencia < 0) return 'text-red-600';
  return 'text-gray-600';
};

const TabHistorial = () => {
  const [filtros, setFiltros] = useState<FiltrosTurnos>({ page: 0 });
  const { sortField, sortDirection, handleSort } = useSort('fechaHoraApertura', 'desc', () =>
    setFiltros((prev) => ({ ...prev, page: 0 }))
  );
  const filtrosConSort: FiltrosTurnos = { ...filtros, sort: `${sortField},${sortDirection}` };
  const { data, isLoading, isFetching } = useTurnos(filtrosConSort);
  const actualizando = isFetching && !isLoading;
  const { data: vendedores } = useVendedoresCaja();
  const [turnoDetalleId, setTurnoDetalleId] = useState<number | null>(null);

  const actualizarFiltro = (cambios: Partial<FiltrosTurnos>) => {
    setFiltros((prev) => ({ ...prev, ...cambios, page: 0 }));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>Vendedor</label>
            <select
              value={filtros.usuarioId ?? ''}
              onChange={(e) => actualizarFiltro({ usuarioId: e.target.value ? Number(e.target.value) : undefined })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Todos</option>
              {vendedores?.map((v) => (
                <option key={v.id} value={v.id}>{v.nombreMostrable}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>Estado</label>
            <select
              value={filtros.estado ?? ''}
              onChange={(e) => actualizarFiltro({ estado: (e.target.value || undefined) as TurnoCajaEstado | undefined })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Todos</option>
              <option value="ABIERTO">Abierto</option>
              <option value="CERRADO">Cerrado</option>
              <option value="CERRADO_FORZADO">Cerrado forzado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>Desde</label>
            <input
              type="date"
              value={filtros.desde ?? ''}
              onChange={(e) => actualizarFiltro({ desde: e.target.value || undefined })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>Hasta</label>
            <input
              type="date"
              value={filtros.hasta ?? ''}
              onChange={(e) => actualizarFiltro({ hasta: e.target.value || undefined })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {!isLoading && (!data || data.content.length === 0) ? (
          <p className="text-center py-12 text-gray-500">No se encontraron turnos con los filtros seleccionados</p>
        ) : (
          <>
            <div className={`overflow-x-auto transition-opacity duration-300 ${actualizando ? 'opacity-60' : ''}`}>
              <table className="w-full table-fixed">
                <thead className="bg-[#F3F4F9]">
                  <tr>
                    <SortableTh field="usuarioNombre" label="Vendedor" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} widthClass="w-[24%]" />
                    <SortableTh field="fechaHoraApertura" label="Apertura" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} widthClass="w-[17%]" />
                    <SortableTh field="fechaHoraCierre" label="Cierre" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} widthClass="w-[17%]" />
                    <SortableTh field="montoInicial" label="Monto inicial" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} align="right" widthClass="w-[16%]" />
                    <SortableTh field="diferenciaEfectivo" label="Diferencia" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} align="right" widthClass="w-[14%]" />
                    <SortableTh field="estado" label="Estado" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} align="center" widthClass="w-[12%]" />
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <TableSkeletonRows columns={6} />
                  ) : (
                    data!.content.map((turno, index) => (
                      <tr
                        key={turno.id}
                        className={`cursor-pointer hover:bg-blue-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        onClick={() => setTurnoDetalleId(turno.id)}
                      >
                        <td className="px-6 py-3 text-sm font-semibold truncate" style={{ color: '#11194F' }}>{turno.usuarioNombre}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{formatearFechaHora(turno.fechaHoraApertura)}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{formatearFechaHora(turno.fechaHoraCierre)}</td>
                        <td className="px-6 py-3 text-sm text-right">{formatCurrency(turno.montoInicial)}</td>
                        <td className="px-6 py-3 text-sm text-right font-semibold">
                          {turno.arqueoRealizado ? (
                            <span className={diferenciaColor(turno.diferenciaEfectivo ?? 0)}>
                              {formatCurrency(turno.diferenciaEfectivo)}
                            </span>
                          ) : (
                            <span className="text-gray-400">Sin arqueo</span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${badgeEstadoTurno(turno.estado)}`}>
                            {turno.estado}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {data && (
              <PaginacionTabla
                page={filtros.page ?? 0}
                totalPages={data.totalPages}
                totalElements={data.totalElements}
                cantidadEnPagina={data.content.length}
                etiqueta="turnos"
                onChange={(page) => setFiltros((prev) => ({ ...prev, page }))}
              />
            )}
          </>
        )}
      </div>

      <HistorialAntiguoSection />

      {turnoDetalleId !== null && (
        <TurnoDetalleModal turnoId={turnoDetalleId} onClose={() => setTurnoDetalleId(null)} />
      )}
    </div>
  );
};

// ---------- Sub-sección: Historial anterior (cuadratura_caja, sistema legacy) ----------
// Se mantiene aparte del historial nuevo (turno_caja) en vez de mezclarse o migrarse:
// son tablas independientes sin relación entre sí. Solo se renderiza si hay registros
// antiguos — un almacén que empezó a usar el sistema después de este módulo no verá nada.

const totalVentasCuadraturaAntigua = (c: { ventasEfectivo: number | null; ventasDebito: number | null; ventasCredito: number | null; ventasFiado: number | null; ventasTransferencia: number | null; otrosIngresos: number | null }) =>
  (c.ventasEfectivo ?? 0) + (c.ventasDebito ?? 0) + (c.ventasCredito ?? 0) + (c.ventasFiado ?? 0) + (c.ventasTransferencia ?? 0) + (c.otrosIngresos ?? 0);

const convertirFechaHoyADDMMYYYY = () => {
  const [year, month, day] = getFechaHoyChile().split('-');
  return `${day}-${month}-${year}`;
};

const HistorialAntiguoSection = () => {
  const addToast = useUIStore((state) => state.addToast);
  const [page, setPage] = useState(0);
  const [expandido, setExpandido] = useState(false);
  const [cajaACerrar, setCajaACerrar] = useState<{ id: number; cajero: string } | null>(null);
  const [cajaDetalle, setCajaDetalle] = useState<CuadraturaCaja | null>(null);
  const { data, refetch } = useCuadraturasConFiltros({ fechaInicio: '01-01-2000', fechaFin: convertirFechaHoyADDMMYYYY(), page });
  const cerrarCaja = useCerrarCajaById();

  const handleCerrar = () => {
    if (!cajaACerrar) return;
    cerrarCaja.mutate(cajaACerrar.id, {
      onSuccess: () => {
        addToast('success', 'Caja del sistema anterior cerrada');
        setCajaACerrar(null);
        refetch();
      },
      onError: (err: any) => addToast('error', err?.message || 'Error al cerrar la caja'),
    });
  };

  // Nada que mostrar (o todavía cargando): no renderizar la sección en absoluto.
  if (!data || data.totalElements === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpandido((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-6 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold" style={{ color: '#11194F' }}>Historial anterior</h2>
          <Badge
            variant="neutral"
            title="Este registro es del módulo de caja anterior. No tiene movimientos asociados; haz clic en una fila para ver el desglose por medio de pago."
          >
            Sistema anterior
          </Badge>
          <span className="text-sm text-gray-400">({data.totalElements})</span>
        </div>
        {expandido ? <MdExpandLess className="text-2xl text-gray-500" /> : <MdExpandMore className="text-2xl text-gray-500" />}
      </button>
      {expandido && (
        <>
      <div className="overflow-x-auto border-t border-gray-200">
        <table className="w-full table-fixed">
          <thead className="bg-[#F3F4F9]">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F] w-[24%]">Vendedor</th>
              <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F] w-[17%]">Apertura</th>
              <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F] w-[17%]">Cierre</th>
              <th className="px-6 py-3 text-right text-sm font-bold text-[#11194F] w-[16%]">Monto inicial</th>
              <th className="px-6 py-3 text-right text-sm font-bold text-[#11194F] w-[14%]">Total ventas</th>
              <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F] w-[12%]">Estado</th>
              <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F] w-[10%]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.content.map((c, index) => (
              <tr
                key={c.id}
                onClick={() => setCajaDetalle(c)}
                className={`cursor-pointer hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
              >
                <td className="px-6 py-3 text-sm font-semibold truncate" style={{ color: '#11194F' }}>{c.cajero}</td>
                <td className="px-6 py-3 text-sm text-gray-600">{c.fechaInicio} {c.horaInicio}</td>
                <td className="px-6 py-3 text-sm text-gray-600">{c.fechaCierre ? `${c.fechaCierre} ${c.horaCierre}` : '-'}</td>
                <td className="px-6 py-3 text-sm text-right">{formatCurrency(c.montoInicial)}</td>
                <td className="px-6 py-3 text-sm text-right">{formatCurrency(totalVentasCuadraturaAntigua(c))}</td>
                <td className="px-6 py-3 text-center">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${c.fechaCierre ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}`}>
                    {c.fechaCierre ? 'CERRADO' : 'ABIERTO'}
                  </span>
                </td>
                <td className="px-6 py-3 text-center">
                  {!c.fechaCierre && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCajaACerrar({ id: c.id, cajero: c.cajero });
                      }}
                      className="text-xs font-semibold px-3 py-1 rounded-full text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Cerrar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cajaDetalle && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setCajaDetalle(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold" style={{ color: '#11194F' }}>Detalle de caja (sistema anterior)</h3>
              <button onClick={() => setCajaDetalle(null)} className="text-gray-400 hover:text-gray-600">
                <MdX className="text-2xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold mb-3" style={{ color: '#11194F' }}>Información general</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-gray-600">Vendedor</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>{cajaDetalle.cajero}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Estado</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${cajaDetalle.fechaCierre ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}`}>
                      {cajaDetalle.fechaCierre ? 'CERRADO' : 'ABIERTO'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Apertura</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>{cajaDetalle.fechaInicio} {cajaDetalle.horaInicio}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cierre</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>
                      {cajaDetalle.fechaCierre ? `${cajaDetalle.fechaCierre} ${cajaDetalle.horaCierre}` : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold mb-3" style={{ color: '#11194F' }}>Montos</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monto inicial:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(cajaDetalle.montoInicial)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas efectivo:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(cajaDetalle.ventasEfectivo ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas débito:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(cajaDetalle.ventasDebito ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas crédito:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(cajaDetalle.ventasCredito ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas fiado:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(cajaDetalle.ventasFiado ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas transferencia:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(cajaDetalle.ventasTransferencia ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Otros ingresos:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatCurrency(cajaDetalle.otrosIngresos ?? 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200 pt-2 mt-2">
                    <span className="font-semibold" style={{ color: '#11194F' }}>Total ventas:</span>
                    <span className="font-bold" style={{ color: '#11194F' }}>{formatCurrency(totalVentasCuadraturaAntigua(cajaDetalle))}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {cajaACerrar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setCajaACerrar(null)}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#11194F' }}>Cerrar caja del sistema anterior</h3>
            <p className="text-sm text-gray-600 mb-4">
              ¿Cerrar la caja de <strong>{cajaACerrar.cajero}</strong>?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
              <p className="text-xs text-amber-800">
                <strong>Ojo:</strong> el sistema anterior recalcula el total de ventas sumando
                todas las boletas del vendedor desde la apertura hasta este momento. Si esta caja
                quedó abierta hace días, ese total probablemente va a incluir ventas de turnos
                posteriores no relacionados — revisa el número resultante, no lo tomes como una
                cuadratura real.
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setCajaACerrar(null)} disabled={cerrarCaja.isPending} className="flex-1">
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleCerrar} isLoading={cerrarCaja.isPending} disabled={cerrarCaja.isPending} className="flex-1">
                Sí, cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
      <PaginacionTabla
        page={page}
        totalPages={data.totalPages}
        totalElements={data.totalElements}
        cantidadEnPagina={data.content.length}
        etiqueta="registros antiguos"
        onChange={setPage}
      />
        </>
      )}
    </div>
  );
};

// ---------- Tab: Aprobaciones ----------

const TabAprobaciones = () => {
  const addToast = useUIStore((state) => state.addToast);
  const puedeAprobar = usePuedeCaja('CAJA_APROBAR_MOVIMIENTOS');
  const puedeRechazar = usePuedeCaja('CAJA_RECHAZAR_MOVIMIENTOS');
  const puedeDeshacer = usePuedeCaja('CAJA_DESHACER_RESOLUCION');
  const [page, setPage] = useState(0);
  const { sortField, sortDirection, handleSort, sortParam } = useSort('fechaHoraCreacion', 'asc', () => setPage(0));
  const { data, isLoading, isFetching } = useMovimientosPendientes(page, sortParam);
  const actualizando = isFetching && !isLoading;
  const aprobar = useAprobarMovimiento();
  const rechazar = useRechazarMovimiento();
  const [movimientoARechazar, setMovimientoARechazar] = useState<number | null>(null);

  const [pageResueltos, setPageResueltos] = useState(0);
  const {
    sortField: sortFieldResueltos,
    sortDirection: sortDirectionResueltos,
    handleSort: handleSortResueltos,
    sortParam: sortParamResueltos,
  } = useSort('fechaHoraCreacion', 'desc', () => setPageResueltos(0));
  const { data: resueltos, isLoading: isLoadingResueltos, isFetching: isFetchingResueltos } = useMovimientosResueltos(pageResueltos, sortParamResueltos);
  const actualizandoResueltos = isFetchingResueltos && !isLoadingResueltos;
  const deshacer = useDeshacerResolucionMovimiento();

  const [pageDeshacerLog, setPageDeshacerLog] = useState(0);
  const {
    sortField: sortFieldDeshacerLog,
    sortDirection: sortDirectionDeshacerLog,
    handleSort: handleSortDeshacerLog,
    sortParam: sortParamDeshacerLog,
  } = useSort('fechaHoraDeshacer', 'desc', () => setPageDeshacerLog(0));
  const { data: deshacerLog, isLoading: isLoadingDeshacerLog, isFetching: isFetchingDeshacerLog } = useMovimientosDeshacerLog(pageDeshacerLog, sortParamDeshacerLog);
  const actualizandoDeshacerLog = isFetchingDeshacerLog && !isLoadingDeshacerLog;

  const handleAprobar = (movimientoId: number) => {
    aprobar.mutate(movimientoId, {
      onSuccess: () => addToast('success', 'Movimiento aprobado'),
      onError: (err: any) => addToast('error', err?.message || 'Error al aprobar el movimiento'),
    });
  };

  const handleRechazar = (motivo: string) => {
    if (movimientoARechazar === null) return;
    rechazar.mutate(
      { movimientoId: movimientoARechazar, dto: { motivo } },
      {
        onSuccess: () => {
          setMovimientoARechazar(null);
          addToast('success', 'Movimiento rechazado');
        },
        onError: (err: any) => addToast('error', err?.message || 'Error al rechazar el movimiento'),
      }
    );
  };

  const handleDeshacer = (movimientoId: number) => {
    deshacer.mutate(movimientoId, {
      onSuccess: () => addToast('success', 'Resolución deshecha: el movimiento vuelve a pendiente'),
      onError: (err: any) => addToast('error', err?.message || 'Error al deshacer la resolución'),
    });
  };

  const aprobacionesSteps = useMemo(
    () => buildAprobacionesSteps({ puedeAprobar, puedeRechazar, puedeDeshacer }),
    [puedeAprobar, puedeRechazar, puedeDeshacer]
  );
  const { start: startAprobacionesTour } = useShepherdTour(aprobacionesSteps, { logoUrl: logo, name: 'Almacenes Digitales' });

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={startAprobacionesTour}
          className="flex items-center gap-1.5 text-sm font-semibold hover:underline"
          style={{ color: '#11194F' }}
        >
          <MdLightbulbOutline className="text-lg" />
          Ver tour de esta sección
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-tour="aprobaciones-pendientes">
        <h2 className="text-lg font-bold px-6 py-4 border-b border-gray-200" style={{ color: '#11194F' }}>
          Pendientes de aprobación
        </h2>
        {!isLoading && (!data || data.content.length === 0) ? (
          <p className="text-center py-12 text-gray-500">No hay movimientos pendientes de aprobación</p>
        ) : (
          <>
            <div className={`overflow-x-auto transition-opacity duration-300 ${actualizando ? 'opacity-60' : ''}`}>
              <table className="w-full">
                <thead className="bg-[#F3F4F9]">
                  <tr>
                    <SortableTh field="id" label="N°" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <SortableTh field="usuarioCreacionNombre" label="Vendedor" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F]">Glosa</th>
                    <SortableTh field="monto" label="Monto" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} align="right" />
                    <SortableTh field="fechaHoraCreacion" label="Fecha" currentField={sortField} currentDirection={sortDirection} onSort={handleSort} />
                    <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <TableSkeletonRows columns={6} />
                  ) : (
                    data!.content.map((m, index) => (
                      <tr key={m.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-3 text-sm text-gray-600">#{m.id}</td>
                        <td className="px-6 py-3 text-sm font-semibold" style={{ color: '#11194F' }}>{m.usuarioCreacionNombre}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{m.glosa}</td>
                        <td className="px-6 py-3 text-sm text-right">{formatCurrency(m.monto)}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{formatearFechaHora(m.fechaHoraCreacion)}</td>
                        <td className="px-6 py-3">
                          <div className="flex items-center justify-center gap-2">
                            {puedeAprobar && (
                              <button
                                onClick={() => handleAprobar(m.id)}
                                disabled={aprobar.isPending}
                                className="p-2 rounded-lg text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                                title="Aprobar"
                              >
                                <MdCheck className="text-xl" />
                              </button>
                            )}
                            {puedeRechazar && (
                              <button
                                onClick={() => setMovimientoARechazar(m.id)}
                                disabled={rechazar.isPending}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                title="Rechazar"
                              >
                                <MdX className="text-xl" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {data && (
              <PaginacionTabla
                page={page}
                totalPages={data.totalPages}
                totalElements={data.totalElements}
                cantidadEnPagina={data.content.length}
                etiqueta="movimientos pendientes"
                onChange={setPage}
              />
            )}
          </>
        )}

        {movimientoARechazar !== null && (
          <RechazarMovimientoModal
            onClose={() => setMovimientoARechazar(null)}
            onConfirmar={handleRechazar}
            isLoading={rechazar.isPending}
          />
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-tour="aprobaciones-resueltos">
        <h2 className="text-lg font-bold px-6 py-4 border-b border-gray-200" style={{ color: '#11194F' }}>
          Resueltos recientemente
        </h2>
        <p className="text-sm text-gray-500 px-6 pb-4">
          ¿Aprobaste o rechazaste algo por error? Puedes deshacerlo mientras el turno siga abierto.
        </p>
        {!isLoadingResueltos && (!resueltos || resueltos.content.length === 0) ? (
          <p className="text-center py-12 text-gray-500">No hay movimientos resueltos recientemente</p>
        ) : (
          <>
            <div className={`overflow-x-auto transition-opacity duration-300 ${actualizandoResueltos ? 'opacity-60' : ''}`}>
              <table className="w-full">
                <thead className="bg-[#F3F4F9]">
                  <tr>
                    <SortableTh field="id" label="N°" currentField={sortFieldResueltos} currentDirection={sortDirectionResueltos} onSort={handleSortResueltos} />
                    <SortableTh field="usuarioCreacionNombre" label="Vendedor" currentField={sortFieldResueltos} currentDirection={sortDirectionResueltos} onSort={handleSortResueltos} />
                    <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F]">Glosa</th>
                    <SortableTh field="monto" label="Monto" currentField={sortFieldResueltos} currentDirection={sortDirectionResueltos} onSort={handleSortResueltos} align="right" />
                    <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F]">Estado</th>
                    <SortableTh field="fechaHoraCreacion" label="Fecha" currentField={sortFieldResueltos} currentDirection={sortDirectionResueltos} onSort={handleSortResueltos} />
                    <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingResueltos ? (
                    <TableSkeletonRows columns={7} />
                  ) : (
                    resueltos!.content.map((m, index) => (
                      <tr key={m.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-3 text-sm text-gray-600">#{m.id}</td>
                        <td className="px-6 py-3 text-sm font-semibold" style={{ color: '#11194F' }}>{m.usuarioCreacionNombre}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{m.glosa}</td>
                        <td className="px-6 py-3 text-sm text-right">{formatCurrency(m.monto)}</td>
                        <td className="px-6 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${badgeEstadoMovimiento(m.estado)}`}>
                            {m.estado}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-600">{formatearFechaHora(m.fechaHoraCreacion)}</td>
                        <td className="px-6 py-3 text-center">
                          {puedeDeshacer && (
                            <button
                              onClick={() => handleDeshacer(m.id)}
                              disabled={deshacer.isPending}
                              className="p-2 rounded-lg text-[#43BFFF] hover:bg-blue-50 transition-colors disabled:opacity-50"
                              title="Deshacer"
                            >
                              <MdUndo className="text-xl" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {resueltos && (
              <PaginacionTabla
                page={pageResueltos}
                totalPages={resueltos.totalPages}
                totalElements={resueltos.totalElements}
                cantidadEnPagina={resueltos.content.length}
                etiqueta="movimientos resueltos"
                onChange={setPageResueltos}
              />
            )}
          </>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-tour="aprobaciones-historial">
        <h2 className="text-lg font-bold px-6 py-4 border-b border-gray-200" style={{ color: '#11194F' }}>
          Historial de deshacer
        </h2>
        <p className="text-sm text-gray-500 px-6 pb-4">
          Auditoría: qué aprobación o rechazo se deshizo, quién lo había resuelto y quién lo deshizo.
        </p>
        {!isLoadingDeshacerLog && (!deshacerLog || deshacerLog.content.length === 0) ? (
          <p className="text-center py-12 text-gray-500">No hay deshacer registrados</p>
        ) : (
          <>
            <div className={`overflow-x-auto transition-opacity duration-300 ${actualizandoDeshacerLog ? 'opacity-60' : ''}`}>
              <table className="w-full">
                <thead className="bg-[#F3F4F9]">
                  <tr>
                    <SortableTh field="movimientoCajaId" label="N°" currentField={sortFieldDeshacerLog} currentDirection={sortDirectionDeshacerLog} onSort={handleSortDeshacerLog} />
                    <SortableTh field="usuarioCreacionNombre" label="Vendedor" currentField={sortFieldDeshacerLog} currentDirection={sortDirectionDeshacerLog} onSort={handleSortDeshacerLog} />
                    <th className="px-6 py-3 text-left text-sm font-bold text-[#11194F]">Glosa</th>
                    <SortableTh field="monto" label="Monto" currentField={sortFieldDeshacerLog} currentDirection={sortDirectionDeshacerLog} onSort={handleSortDeshacerLog} align="right" />
                    <th className="px-6 py-3 text-center text-sm font-bold text-[#11194F]">Estado anterior</th>
                    <SortableTh field="fechaHoraResolucion" label="Resuelto por" currentField={sortFieldDeshacerLog} currentDirection={sortDirectionDeshacerLog} onSort={handleSortDeshacerLog} />
                    <SortableTh field="fechaHoraDeshacer" label="Deshecho por" currentField={sortFieldDeshacerLog} currentDirection={sortDirectionDeshacerLog} onSort={handleSortDeshacerLog} />
                  </tr>
                </thead>
                <tbody>
                  {isLoadingDeshacerLog ? (
                    <TableSkeletonRows columns={7} />
                  ) : (
                    deshacerLog!.content.map((log, index) => (
                      <tr key={log.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-3 text-sm text-gray-600">#{log.movimientoCajaId}</td>
                        <td className="px-6 py-3 text-sm font-semibold" style={{ color: '#11194F' }}>{log.usuarioCreacionNombre}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{log.glosa}</td>
                        <td className="px-6 py-3 text-sm text-right">{formatCurrency(log.monto)}</td>
                        <td className="px-6 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${badgeEstadoMovimiento(log.estadoAnterior)}`}>
                            {log.estadoAnterior}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-600">
                          <div>{log.usuarioResolucionNombre}</div>
                          <div className="text-xs text-gray-400">{formatearFechaHora(log.fechaHoraResolucion)}</div>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-600">
                          <div>{log.usuarioDeshaceNombre}</div>
                          <div className="text-xs text-gray-400">{formatearFechaHora(log.fechaHoraDeshacer)}</div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {deshacerLog && (
              <PaginacionTabla
                page={pageDeshacerLog}
                totalPages={deshacerLog.totalPages}
                totalElements={deshacerLog.totalElements}
                cantidadEnPagina={deshacerLog.content.length}
                etiqueta="deshacer"
                onChange={setPageDeshacerLog}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---------- Tab: Configuración ----------

const TabConfiguracion = () => {
  const addToast = useUIStore((state) => state.addToast);
  const { data: configuracion, isLoading } = useConfiguracionCaja();
  const actualizar = useActualizarConfiguracionCaja();

  const [arqueoObligatorio, setArqueoObligatorio] = useState<boolean | null>(null);
  const [aprobacionMovimientos, setAprobacionMovimientos] = useState<boolean | null>(null);

  const valorArqueo = arqueoObligatorio ?? configuracion?.arqueoObligatorio ?? false;
  const valorAprobacion = aprobacionMovimientos ?? configuracion?.aprobacionMovimientos ?? false;
  const hayCambios =
    !!configuracion &&
    (valorArqueo !== configuracion.arqueoObligatorio || valorAprobacion !== configuracion.aprobacionMovimientos);

  const handleGuardar = () => {
    actualizar.mutate(
      { arqueoObligatorio: valorArqueo, aprobacionMovimientos: valorAprobacion },
      {
        onSuccess: () => {
          setArqueoObligatorio(null);
          setAprobacionMovimientos(null);
          addToast('success', 'Configuración actualizada');
        },
        onError: (err: any) => addToast('error', err?.message || 'Error al actualizar la configuración'),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl bg-white rounded-xl border border-gray-200 p-6 divide-y divide-gray-200">
      <ToggleSwitch
        checked={valorArqueo}
        onChange={(v) => setArqueoObligatorio(v)}
        label="Arqueo obligatorio"
        descripcion="Exige contar el efectivo del cajón al cerrar la caja."
        disabled={actualizar.isPending}
      />
      <ToggleSwitch
        checked={valorAprobacion}
        onChange={(v) => setAprobacionMovimientos(v)}
        label="Aprobación de movimientos"
        descripcion="Los retiros, gastos e ingresos requieren tu aprobación antes de afectar la caja."
        disabled={actualizar.isPending}
      />
      <div className="pt-4">
        <Button
          variant="primary"
          onClick={handleGuardar}
          isLoading={actualizar.isPending}
          disabled={!hayCambios || actualizar.isPending}
        >
          Guardar cambios
        </Button>
      </div>
    </div>
  );
};

// ---------- Tour guiado: Aprobaciones (sub-tour, solo manual) ----------
// A diferencia del tour general de la página (auto-trigger la primera vez),
// este es solo bajo demanda: la pestaña ya tiene 3 bloques con varios casos
// de uso cada uno, así que un tour más al entrar sería demasiado — el
// usuario lo pide con su propio botón cuando lo necesita.

interface BuildAprobacionesStepsParams {
  puedeAprobar: boolean;
  puedeRechazar: boolean;
  puedeDeshacer: boolean;
}

const buildAprobacionesSteps = ({
  puedeAprobar,
  puedeRechazar,
  puedeDeshacer,
}: BuildAprobacionesStepsParams): TourStepConfig[] => {
  const acciones = [puedeAprobar && 'aprobar', puedeRechazar && 'rechazar'].filter(Boolean) as string[];

  return [
    {
      id: 'pendientes',
      title: 'Pendientes de aprobación',
      text:
        acciones.length > 0
          ? `Retiros, gastos e ingresos extra que otros vendedores registraron y esperan que los ${acciones.join(' o ')}.`
          : 'Retiros, gastos e ingresos extra que otros vendedores registraron y están a la espera de que alguien los apruebe o rechace.',
      attachTo: { element: '[data-tour="aprobaciones-pendientes"]', on: 'bottom' },
    },
    {
      id: 'resueltos',
      title: 'Resueltos recientemente',
      text: puedeDeshacer
        ? 'Lo que ya aprobaste o rechazaste. Si te equivocaste, puedes deshacerlo mientras el turno del vendedor siga abierto.'
        : 'Lo que ya se aprobó o rechazó recientemente en el almacén.',
      attachTo: { element: '[data-tour="aprobaciones-resueltos"]', on: 'top' },
    },
    {
      id: 'historial-deshacer',
      title: 'Historial de deshacer',
      text: 'Auditoría de todo lo que se deshizo: quién había resuelto el movimiento y quién lo deshizo después.',
      attachTo: { element: '[data-tour="aprobaciones-historial"]', on: 'top' },
    },
  ];
};

// ---------- Permisos: liberado temporalmente ----------
// A pedido del negocio, la pestaña Permisos queda accesible sin plan Premium
// durante un periodo promocional — pero sigue mostrando el badge "Premium"
// para dejar claro que después de esta fecha pasa a requerirlo. Chequeo por
// fecha (no código comentado a mano): el gate se reactiva solo al vencer,
// sin depender de que alguien vuelva a tocar este archivo.
const PERMISOS_LIBERADO_HASTA = new Date('2026-11-04T00:00:00');

// ---------- Tour guiado ----------

const TOUR_ID = 'caja-360-administracion';
const TOUR_VERSION = 'v1';

interface BuildAdminStepsParams {
  esPropietario: boolean;
  hasPremiumAccess: boolean;
  permisosLiberado: boolean;
  puedeVerPendientes: boolean;
  puedeConfigurar: boolean;
  puedeAbrirParaOtro: boolean;
  setTab: (tab: Tab) => void;
}

const buildAdminSteps = ({
  esPropietario,
  hasPremiumAccess,
  permisosLiberado,
  puedeVerPendientes,
  puedeConfigurar,
  puedeAbrirParaOtro,
  setTab,
}: BuildAdminStepsParams): TourStepConfig[] => {
  const steps: TourStepConfig[] = [
    {
      id: 'intro',
      title: 'Administrar cajas',
      text: 'Acá ves y gestionas la caja de todo el almacén, no solo la tuya.',
      attachTo: { element: '[data-tour="admin-header"]', on: 'bottom' },
    },
    {
      id: 'tab-abiertos',
      title: 'Turnos abiertos',
      text: 'Los turnos de caja que están abiertos ahora mismo en el almacén.',
      attachTo: { element: '[data-tour="admin-tab-abiertos"]', on: 'bottom' },
      beforeShow: () => setTab('abiertos'),
    },
  ];

  if (puedeAbrirParaOtro) {
    steps.push({
      id: 'abrir-vendedor',
      title: 'Abrir caja para un vendedor',
      text: 'Puedes abrir el turno de otro vendedor por él, por ejemplo si se le olvidó hacerlo.',
      attachTo: { element: '[data-tour="admin-abrir-vendedor"]', on: 'bottom' },
      beforeShow: () => setTab('abiertos'),
      waitForSelector: true,
    });
  }

  steps.push({
    id: 'tab-historial',
    title: 'Historial y diferencias',
    text: 'Los turnos ya cerrados, con las diferencias de arqueo cuando las hubo.',
    attachTo: { element: '[data-tour="admin-tab-historial"]', on: 'bottom' },
  });

  if (puedeVerPendientes) {
    steps.push({
      id: 'tab-aprobaciones',
      title: 'Aprobaciones',
      text: 'Retiros, gastos e ingresos extra pendientes de aprobación, lo resuelto recientemente y el historial de deshacer.',
      attachTo: { element: '[data-tour="admin-tab-aprobaciones"]', on: 'bottom' },
    });
  }

  if (puedeConfigurar) {
    steps.push({
      id: 'tab-configuracion',
      title: 'Configuración',
      text: 'Define si el arqueo es obligatorio al cerrar turno y si los movimientos necesitan aprobación.',
      attachTo: { element: '[data-tour="admin-tab-configuracion"]', on: 'bottom' },
    });
  }

  if (esPropietario) {
    steps.push({
      id: 'tab-permisos',
      title: 'Permisos por vendedor',
      text:
        !hasPremiumAccess && permisosLiberado
          ? 'Delega en tus vendedores de confianza qué acciones de caja pueden hacer, sin depender de ti. Disponible para todos hasta el 4 de noviembre de 2026 — después, algunas funcionalidades podrían pasar a Premium.'
          : 'Delega en tus vendedores de confianza qué acciones de caja pueden hacer, sin depender de ti.',
      attachTo: { element: '[data-tour="admin-tab-permisos"]', on: 'bottom' },
    });
  }

  return steps;
};

// ---------- Página ----------

export const CajaAdministracionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabInicial = searchParams.get('tab');
  const [tab, setTab] = useState<Tab>(
    tabs.some((t) => t.id === tabInicial) ? (tabInicial as Tab) : 'abiertos'
  );

  const session = useAuthStore((state) => state.session);
  const esPropietario = session?.user?.esPropietario ?? false;
  const hasPremiumAccess = session?.hasPremiumAccess ?? false;
  const permisosLiberado = new Date() < PERMISOS_LIBERADO_HASTA;
  const puedeUsarPermisos = hasPremiumAccess || permisosLiberado;
  const puedeVerPendientes = usePuedeCaja('CAJA_VER_PENDIENTES');
  const puedeConfigurar = usePuedeCaja('CAJA_CONFIGURAR');
  const puedeAbrirParaOtro = usePuedeCaja('CAJA_ABRIR_PARA_OTRO');

  // "Turnos abiertos"/"Historial" son visibles para cualquiera (el backend
  // no las gatea con un permiso puntual, solo filtra qué turnos ve cada
  // quien) — el resto depende del permiso concreto de esta cuenta.
  const tabsVisibles = tabs.filter((t) => {
    if (t.id === 'aprobaciones') return puedeVerPendientes;
    if (t.id === 'configuracion') return puedeConfigurar;
    if (t.id === 'permisos') return esPropietario;
    return true;
  });

  const steps = useMemo(
    () => buildAdminSteps({ esPropietario, hasPremiumAccess, permisosLiberado, puedeVerPendientes, puedeConfigurar, puedeAbrirParaOtro, setTab }),
    [esPropietario, hasPremiumAccess, permisosLiberado, puedeVerPendientes, puedeConfigurar, puedeAbrirParaOtro]
  );
  const { start } = useShepherdTour(steps, { logoUrl: logo, name: 'Almacenes Digitales' });
  const { seen, markAsSeen } = useTourPersistido(TOUR_ID, TOUR_VERSION);

  useEffect(() => {
    if (!seen) {
      start();
      markAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen]);

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      <TourFab onClick={start} />

      <div className="mb-6 flex items-center gap-4" data-tour="admin-header">
        <button onClick={() => navigate('/caja-360')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <MdArrowBack className="text-2xl" style={{ color: '#43BFFF' }} />
        </button>
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>Administrar cajas</h1>
          <p className="text-gray-600 mt-1">Turnos abiertos, historial, aprobaciones y configuración</p>
        </div>
      </div>

      {!hasPremiumAccess && permisosLiberado && (
        <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium mb-6" style={{ color: '#11194F' }}>
          <MdInfoOutline className="shrink-0 text-lg" />
          <span>
            Algunas funcionalidades de este módulo estarán disponibles para todos hasta el{' '}
            {PERMISOS_LIBERADO_HASTA.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}. Después de esa fecha, podrían pasar a Premium.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
        {tabsVisibles.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-tour={`admin-tab-${t.id}`}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
              tab === t.id ? 'border-[#43BFFF] text-[#11194F]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'abiertos' && <TabTurnosAbiertos />}
      {tab === 'historial' && <TabHistorial />}
      {tab === 'aprobaciones' && puedeVerPendientes && <TabAprobaciones />}
      {tab === 'configuracion' && puedeConfigurar && <TabConfiguracion />}
      {tab === 'permisos' && esPropietario && (
        puedeUsarPermisos ? (
          <TabPermisos />
        ) : (
          <PremiumGate
            title="Permisos por vendedor"
            description="Delega tareas de caja en tus vendedores de confianza sin tener que estar encima de ellos. Disponible en el plan Premium."
            ctaLabel="Mejorar mi plan"
            fullScreen={false}
          />
        )
      )}
    </div>
  );
};
